import React, { useState, useLayoutEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { neighborhoods } from '../constants/neighborhoods';
import { sizedImage } from '../utils/imageUrl';
import FadeInSection from './FadeInSection';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import '../styles/Neighborhoods.scss';

const gridVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'San Francisco', value: 'sf' },
  { label: 'Peninsula', value: 'peninsula' },
];

function Neighborhoods() {
  const [active, setActive] = useState('all');

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filtered = active === 'all' ? neighborhoods : neighborhoods.filter(n => n.region === active);

  return (
    <>
      <Helmet>
        <title>SF & Peninsula Neighborhood Guides — Market Data & Local Life | Lily Lai Real Estate</title>
        <meta name="description" content="In-depth guides to San Francisco and Peninsula neighborhoods — median home prices, walk scores, local restaurants, schools, and market trends. Expert insights from Lily Lai, Bay Area real estate agent." />
        <link rel="canonical" href="https://www.lilylaisf.com/neighborhoods" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="SF & Peninsula Neighborhood Guides | Lily Lai Real Estate" />
        <meta property="og:description" content="In-depth guides to 23 San Francisco and Peninsula neighborhoods — market data, local restaurants, schools, and what makes each one unique." />
        <meta property="og:url" content="https://www.lilylaisf.com/neighborhoods" />
        <meta property="og:site_name" content="Lily Lai Real Estate" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="SF & Peninsula Neighborhood Guides | Lily Lai Real Estate" />
        <meta name="twitter:description" content="In-depth guides to 23 San Francisco and Peninsula neighborhoods — market data, local restaurants, schools, and what makes each one unique." />

        {/* JSON-LD: ItemList + BreadcrumbList */}
        <script type="application/ld+json">{JSON.stringify([
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.lilylaisf.com/" },
              { "@type": "ListItem", "position": 2, "name": "Neighborhoods", "item": "https://www.lilylaisf.com/neighborhoods" },
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "San Francisco & Peninsula Neighborhood Guides",
            "description": "In-depth real estate and lifestyle guides for San Francisco and Peninsula neighborhoods.",
            "url": "https://www.lilylaisf.com/neighborhoods",
            "itemListElement": neighborhoods.map((n, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "name": n.name,
              "url": `https://www.lilylaisf.com/neighborhoods/${n.slug}`,
              "description": n.tagline,
            }))
          },
          {
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Lily Lai Real Estate",
            "url": "https://www.lilylaisf.com",
            "telephone": "+14157282623",
            "email": "lily@lilylaisf.com",
            "areaServed": ["San Francisco", "San Mateo County"],
            "description": "Lily Lai is a Bay Area real estate agent specializing in San Francisco and Peninsula neighborhoods."
          }
        ])}</script>
      </Helmet>
      <div className="neighborhoods-page">
        <FadeInSection>
          <div className="neighborhoods-hero">
            <p className="neighborhoods-eyebrow">Explore</p>
            <h1>Neighborhood Guides</h1>
            <p className="neighborhoods-subtitle">
              In-depth guides to San Francisco and Peninsula neighborhoods — market data, local favorites, and what makes each one unique.
            </p>
          </div>
        </FadeInSection>

        <FadeInSection>
          <div className="neighborhoods-filters">
            {FILTERS.map(f => (
              <button
                key={f.value}
                className={`neighborhoods-filter-btn ${active === f.value ? 'active' : ''}`}
                onClick={() => {
                  setActive(f.value);
                  if (typeof window.gtag === 'function') window.gtag('event', 'neighborhood_filter_click', { filter_value: f.value });
                }}
                aria-pressed={active === f.value}
              >
                {f.label}
              </button>
            ))}
          </div>
        </FadeInSection>

        <motion.div
          key={active}
          className="neighborhoods-grid"
          variants={gridVariants}
          initial="hidden"
          animate="visible"
        >
          {filtered.map(n => (
            <motion.div key={n.slug} variants={cardVariants}>
              <NavLink
                to={`/neighborhoods/${n.slug}`}
                className="neighborhood-card"
                onClick={() => { if (typeof window.gtag === 'function') window.gtag('event', 'neighborhood_card_click', { neighborhood_name: n.name }); }}
              >
                <div className="neighborhood-card-image">
                  {n.image
                    ? <img src={sizedImage(n.image, 600)} alt={n.name} loading="lazy" />
                    : <div className="neighborhood-card-placeholder" />
                  }
                  <div className="neighborhood-card-overlay">
                    <span className="neighborhood-card-region">
                      {n.region === 'sf' ? 'San Francisco' : 'Peninsula'}
                    </span>
                    <div className="neighborhood-card-info">
                      <h2 className="neighborhood-card-name">{n.name}</h2>
                      <p className="neighborhood-card-tagline">{n.tagline}</p>
                    </div>
                  </div>
                </div>
              </NavLink>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </>
  );
}

export default Neighborhoods;
